import { Fragment, useState } from 'react'
import type { RideAttraction, DimensionMeta } from '../../hooks/useRideIntelligence'

const TYPE_GROUP_ORDER = ['Adult Ride', 'Kids Ride', 'Game', 'Food Stand', 'Gate'] as const

const TYPE_HEADER_COLORS: Record<string, string> = {
  'Adult Ride': 'bg-[#0282bf]/10 text-[#0282bf] dark:bg-[#0282bf]/20',
  'Kids Ride': 'bg-[#0a9356]/10 text-[#0a9356] dark:bg-[#0a9356]/20',
  'Game': 'bg-[#0a9356]/10 text-[#0a9356] dark:bg-[#0a9356]/20',
  'Food Stand': 'bg-[#ffa726]/10 text-[#ffa726] dark:bg-[#ffa726]/20',
  'Gate': 'bg-[#CF4821]/10 text-[#CF4821] dark:bg-[#CF4821]/20',
}

function fmtVal(key: string, val: number | null | undefined): string {
  if (val == null || !Number.isFinite(val)) return '—'
  if (key.includes('earnings') || key.includes('revenue') || key === 'est_earnings_per_guest' || key === 'est_earnings_per_scan') {
    return val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }
  if (key === 'repeat_rate') return `${val.toFixed(2)}x`
  if (key === 'breakage_rate') return `${(val * 100).toFixed(1)}%`
  return val.toLocaleString()
}

interface RankingsPanelProps {
  attractions: RideAttraction[]
  dimensions: DimensionMeta[]
  loading?: boolean
  'data-testid'?: string
}

export function RankingsPanel({
  attractions,
  dimensions,
  loading = false,
  'data-testid': testId,
}: RankingsPanelProps) {
  const [activeDim, setActiveDim] = useState(dimensions[0]?.key ?? 'estimated_earnings')

  if (loading) return <RankingsPanelSkeleton />

  const dim = dimensions.find(d => d.key === activeDim) ?? dimensions[0]
  if (!dim) return null

  const sorted = [...attractions]
    .filter(a => a[dim.key as keyof RideAttraction] != null && Number(a[dim.key as keyof RideAttraction]) > 0)
    .sort((a, b) => Number(b[dim.key as keyof RideAttraction] ?? 0) - Number(a[dim.key as keyof RideAttraction] ?? 0))

  const grouped = new Map<string, { rank: number; attraction: RideAttraction }[]>()
  let rank = 0
  for (const a of sorted) {
    rank++
    const t = a.attraction_type || 'Unknown'
    if (!grouped.has(t)) grouped.set(t, [])
    grouped.get(t)!.push({ rank, attraction: a })
  }

  const visibleTabs = dimensions.slice(0, 4)
  const overflowTabs = dimensions.slice(4)

  return (
    <div data-testid={testId ?? 'rankings-panel'} className="rounded-card border border-hairline bg-white shadow-card-soft dark:border-slate-700 dark:bg-slate-800">
      {/* Dimension tabs */}
      <div className="flex items-center gap-1 overflow-x-auto border-b border-hairline px-3 pt-3 dark:border-slate-700">
        {visibleTabs.map(d => (
          <button
            key={d.key}
            onClick={() => setActiveDim(d.key)}
            className={`shrink-0 rounded-t-lg px-3 py-1.5 text-xs font-medium transition
              ${activeDim === d.key
                ? 'bg-butler-50 text-butler-700 dark:bg-butler-900/30 dark:text-butler-300'
                : 'text-ink-soft hover:bg-slate-50 hover:text-ink dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-200'
              }`}
          >
            {d.label}
          </button>
        ))}
        {overflowTabs.length > 0 && (
          <select
            value={overflowTabs.some(d => d.key === activeDim) ? activeDim : ''}
            onChange={(e) => { if (e.target.value) setActiveDim(e.target.value) }}
            className={`shrink-0 rounded-t-lg border-none bg-transparent px-2 py-1.5 text-xs font-medium outline-none
              ${overflowTabs.some(d => d.key === activeDim)
                ? 'text-butler-700 dark:text-butler-300'
                : 'text-ink-soft dark:text-slate-400'
              }`}
          >
            <option value="">+{overflowTabs.length} more</option>
            {overflowTabs.map(d => (
              <option key={d.key} value={d.key}>{d.label}</option>
            ))}
          </select>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-hairline text-[10px] font-semibold uppercase tracking-wider text-ink-soft dark:border-slate-700 dark:text-slate-400">
              <th className="px-3 py-2 w-8">#</th>
              <th className="px-3 py-2">Attraction</th>
              <th className="px-3 py-2">Type</th>
              <th className="px-3 py-2 text-right">{dim.label}</th>
              <th className="px-3 py-2 text-right">Scans</th>
              <th className="px-3 py-2 text-right">Guests</th>
              <th className="px-3 py-2 text-right">Repeat</th>
            </tr>
          </thead>
          <tbody>
            {TYPE_GROUP_ORDER.map(type => {
              const group = grouped.get(type)
              if (!group || group.length === 0) return null
              const headerColor = TYPE_HEADER_COLORS[type] ?? 'bg-slate-100 text-slate-600'

              return (
                <Fragment key={type}>
                  <tr>
                    <td colSpan={7} className={`px-3 py-1 text-[10px] font-bold uppercase tracking-widest ${headerColor}`}>
                      {type}s ({group.length})
                    </td>
                  </tr>
                  {group.map(({ rank: r, attraction: a }) => (
                    <tr
                      key={a.attraction_id}
                      className="border-b border-hairline/50 transition hover:bg-slate-50 dark:border-slate-700/50 dark:hover:bg-slate-700/30"
                    >
                      <td className="px-3 py-1.5 font-mono text-xs text-ink-soft dark:text-slate-400">{r}</td>
                      <td className="px-3 py-1.5 font-medium text-ink dark:text-slate-100">{a.attraction_name}</td>
                      <td className="px-3 py-1.5 text-xs text-ink-soft dark:text-slate-400">{a.attraction_type}</td>
                      <td className="px-3 py-1.5 text-right font-mono font-bold text-butler-700 dark:text-butler-300">
                        {fmtVal(dim.key, a[dim.key as keyof RideAttraction] as number)}
                      </td>
                      <td className="px-3 py-1.5 text-right font-mono text-xs text-ink-soft dark:text-slate-400">{a.total_scans.toLocaleString()}</td>
                      <td className="px-3 py-1.5 text-right font-mono text-xs text-ink-soft dark:text-slate-400">{a.unique_guests.toLocaleString()}</td>
                      <td className="px-3 py-1.5 text-right font-mono text-xs text-ink-soft dark:text-slate-400">{a.repeat_rate?.toFixed(2) ?? '—'}x</td>
                    </tr>
                  ))}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className="border-t border-hairline px-3 py-2 text-[10px] text-ink-soft dark:border-slate-700 dark:text-slate-400">
        {sorted.length} attractions ranked by {dim.label}
      </div>
    </div>
  )
}

function RankingsPanelSkeleton() {
  return (
    <div className="animate-pulse rounded-card border border-hairline bg-white p-4 shadow-card-soft dark:border-slate-700 dark:bg-slate-800">
      <div className="flex gap-2">
        {[0, 1, 2, 3].map(i => (
          <div key={i} className="h-7 w-24 rounded bg-slate-200 dark:bg-slate-700" />
        ))}
      </div>
      <div className="mt-4 space-y-2">
        {Array.from({ length: 8 }, (_, i) => (
          <div key={i} className="h-6 w-full rounded bg-slate-200 dark:bg-slate-700" />
        ))}
      </div>
    </div>
  )
}
